import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, CheckCircle2, Archive, Loader2, Reply, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new:      "bg-amber-500/20 text-amber-300 border-amber-500/30",
  read:     "bg-blue-500/20 text-blue-300 border-blue-500/30",
  replied:  "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  archived: "bg-white/10 text-white/40 border-white/10",
};

const CATEGORY_LABELS = {
  general: "Général", support: "Support", partnership: "Partenariat", press: "Presse", bug: "Bug",
};

export default function ContactInbox({ token }) {
  const [msgs, setMsgs] = useState([]);
  const [counts, setCounts] = useState({ new: 0, read: 0, replied: 0, archived: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("new");
  const [busy, setBusy] = useState(null);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");

  const sendReply = async (id) => {
    if (replyText.trim().length < 2) { toast.error("Rédigez votre réponse d'abord"); return; }
    setBusy(id);
    try {
      await axios.post(`${API}/api/contact/${id}/reply`, { reply: replyText.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Réponse envoyée par email ✉️");
      setReplyOpen(null); setReplyText("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi de la réponse");
    } finally {
      setBusy(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/contact/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMsgs(res.data.messages || []);
      setCounts(res.data.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger les messages");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await axios.put(`${API}/api/contact/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Marqué "${status}"`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur mise à jour");
    } finally {
      setBusy(null);
    }
  };

  const filtered = msgs.filter((m) => filter === "all" ? true : m.status === filter);

  return (
    <div className="mt-10" data-testid="founder-contact-inbox">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Mail size={20} className="text-accent-blue" />
            Boîte de contact
          </h2>
          <p className="text-xs text-white/50 mt-1">Messages reçus via /contact — cliquer pour lire, archiver, marquer comme répondu.</p>
        </div>
        <div className="flex items-center gap-1.5">
          {["new", "read", "replied", "archived", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${filter === s ? "bg-accent-blue/20 border border-accent-blue/40 text-accent-blue" : "border border-white/10 hover:border-white/30"}`}
              data-testid={`contact-filter-${s}`}
            >
              {s === "all" ? "Tout" : s} {s !== "all" && `(${counts[s] || 0})`}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-white/40" /></div>
      ) : filtered.length === 0 ? (
        <div className="panel p-8 text-center text-white/40">Aucun message</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => (
            <div key={m.id} className="panel p-4 space-y-2" data-testid={`contact-msg-${m.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${STATUS_COLORS[m.status]}`}>{m.status}</span>
                    <span className="text-[10px] uppercase tracking-widest text-white/40">{CATEGORY_LABELS[m.category] || m.category}</span>
                    <span className="text-[10px] text-white/40">· {new Date(m.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <div className="font-semibold">{m.subject}</div>
                  <div className="text-xs text-white/60 mt-0.5">{m.name} · <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`} className="text-accent-blue hover:underline">{m.email}</a></div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setReplyOpen(replyOpen === m.id ? null : m.id); setReplyText(""); }} disabled={busy === m.id} className="btn text-[10px] px-2 py-1 border-accent-blue/40 text-accent-blue hover:bg-accent-blue/10 flex items-center gap-1" data-testid={`contact-reply-btn-${m.id}`}><Reply size={10} /> Répondre</button>
                  {m.status !== "read" && m.status !== "replied" && (
                    <button onClick={() => setStatus(m.id, "read")} disabled={busy === m.id} className="btn text-[10px] px-2 py-1 border-blue-500/30 hover:bg-blue-500/10" data-testid={`contact-mark-read-${m.id}`}>Lu</button>
                  )}
                  {m.status !== "replied" && (
                    <button onClick={() => setStatus(m.id, "replied")} disabled={busy === m.id} className="btn text-[10px] px-2 py-1 border-emerald-500/30 hover:bg-emerald-500/10 flex items-center gap-1" data-testid={`contact-mark-replied-${m.id}`}><CheckCircle2 size={10} /> Répondu</button>
                  )}
                  {m.status !== "archived" && (
                    <button onClick={() => setStatus(m.id, "archived")} disabled={busy === m.id} className="btn text-[10px] px-2 py-1 border-white/20 hover:bg-white/5 flex items-center gap-1" data-testid={`contact-mark-archived-${m.id}`}><Archive size={10} /> Archiver</button>
                  )}
                </div>
              </div>
              <div className="text-sm text-white/80 whitespace-pre-line pt-2 border-t border-white/5">{m.message}</div>
              {m.reply && (
                <div className="text-xs text-white/60 whitespace-pre-line pt-2 pl-3 border-l-2 border-emerald-500/40" data-testid={`contact-reply-sent-${m.id}`}>
                  <span className="text-emerald-300 font-medium">Votre réponse ({m.replied_at ? new Date(m.replied_at).toLocaleString("fr-FR") : ""}) :</span>
                  <div className="mt-1">{m.reply}</div>
                </div>
              )}
              {replyOpen === m.id && (
                <div className="pt-2 space-y-2" data-testid={`contact-reply-form-${m.id}`}>
                  <textarea
                    className="field w-full h-24 text-sm"
                    placeholder={`Votre réponse à ${m.name} — elle partira par email depuis contact@anatradex.com, avec son message cité en dessous.`}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    data-testid={`contact-reply-textarea-${m.id}`}
                  />
                  <div className="flex gap-2">
                    <button onClick={() => sendReply(m.id)} disabled={busy === m.id} className="btn btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5" data-testid={`contact-reply-send-${m.id}`}>
                      {busy === m.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Envoyer la réponse
                    </button>
                    <button onClick={() => { setReplyOpen(null); setReplyText(""); }} className="btn text-xs px-3 py-1.5">Annuler</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
